import React from "react";
import Icon from "./AppIcon";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, reported: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    error.__ErrorBoundary = true;
    this.setState({ errorInfo });
    window.__COMPONENT_ERROR__?.(error, errorInfo);
  }

  handleReportBug = () => {
    const { error, errorInfo } = this.state;
    const errorMessage = error?.message || "Unknown error";
    const errorStack = error?.stack || "";
    const componentStack = errorInfo?.componentStack || "";
    const timestamp = new Date().toISOString();
    const url = window.location.href;

    // Create bug report object
    const bugReport = {
      id: `bug-${Date.now()}`,
      module: "RUNTIME",
      severity: "BUGS",
      title: `Runtime Error: ${errorMessage.slice(0, 80)}`,
      description: `URL: ${url}\nTimestamp: ${timestamp}\n\nError: ${errorMessage}\n\nStack:\n${errorStack}\n\nComponent Stack:\n${componentStack}`,
      reportedAt: timestamp,
    };

    // Store in localStorage for devChecklist to pick up
    try {
      const existingBugs = JSON.parse(localStorage.getItem("reported_bugs") || "[]");
      existingBugs.unshift(bugReport);
      // Keep only last 50 bugs
      localStorage.setItem("reported_bugs", JSON.stringify(existingBugs.slice(0, 50)));
      this.setState({ reported: true });
    } catch (e) {
      console.error("Failed to save bug report:", e);
    }
  };

  render() {
    if (this.state?.hasError) {
      const { error, reported } = this.state;
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-900">
          <div className="text-center p-8 max-w-md">
            <div className="flex justify-center items-center gap-3 mb-6">
              <img 
                src="/assets/images/BlackLogo-1773090086774.png" 
                alt="Logo" 
                className="h-12 w-auto invert"
              />
              <svg xmlns="http://www.w3.org/2000/svg" width="48px" height="48px" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 17 10 11 4 5"></polyline>
                <line x1="12" y1="19" x2="20" y2="19"></line>
              </svg>
            </div>
            <div className="flex flex-col gap-1 text-center">
              <h1 className="text-2xl font-medium text-neutral-100">Something went wrong</h1>
              <p className="text-neutral-400 text-base w-8/12 mx-auto">We encountered an unexpected error while processing your request.</p>
            </div>
            {error?.message && (
              <div className="mt-4 p-3 rounded-lg bg-neutral-800 border border-neutral-700 text-left">
                <p className="text-xs font-mono text-red-400 break-all">{error.message}</p>
              </div>
            )}
            <div className="flex justify-center items-center gap-3 mt-6">
              <button
                onClick={() => {
                  window.location.href = "/";
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200 shadow-sm"
              >
                <Icon name="ArrowLeft" size={18} color="#fff" />
                Back
              </button>
              <button
                onClick={this.handleReportBug}
                disabled={reported}
                className={`font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200 shadow-sm ${
                  reported 
                    ? "bg-green-600 text-white cursor-default" 
                    : "bg-neutral-700 hover:bg-neutral-600 text-neutral-200"
                }`}
              >
                <Icon name={reported ? "CheckCircle" : "Bug"} size={18} color={reported ? "#fff" : "#a3a3a3"} />
                {reported ? "Reported" : "Report to Developer"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props?.children;
  }
}

export default ErrorBoundary;